import { useMemo, useSyncExternalStore } from 'react';
import { useRecoilValue } from 'recoil';
import type { Environment } from '~/engine/data/Environment';
import type { EnvironmentViews } from '~/engine/view/EnvironmentViews';
import { FusedImageDisplay } from '~/engine/view/layers/fusedImageDisplay';
import { viewShowsAcquiredPrimarySlice } from '~/engine/view/planeViewGeometry';
import type { PlaneView } from '~/engine/view/views/PlaneView';
import type { ViewManager } from '~/engine/view/ViewManager';
import { viewManagerState } from '~/state/atoms/engineAtoms';
import { sameElements } from '~/utils/array';
import { useSubscriptionVersion } from './useSubscriptionVersion';

const NO_VIEWS: readonly PlaneView[] = Object.freeze([]);

const noSignal = () => () => {};
const noViews = () => NO_VIEWS;

/**
 * An {@link EnvironmentViews} handle read as a store: the handle returns a
 * fresh array per read, and a store snapshot must keep its identity while
 * the membership is unchanged, so the last list read is held and returned
 * again until a view joins or leaves.
 */
function viewStoreOf(handle: EnvironmentViews) {
    let last: readonly PlaneView[] = NO_VIEWS;
    return {
        subscribe: (listener: () => void) => handle.subscribe(listener),
        read: (): readonly PlaneView[] => {
            const views = handle.current;
            if (!sameElements(last, views)) {
                last = views;
            }
            return last;
        },
    };
}

/**
 * The views an Environment is drawn on, the grid tiles that show it, in
 * registration order. The list
 * keeps its identity while its membership is unchanged, so an effect can
 * key on it and re-run only when a view of the Environment mounts or
 * unmounts. One handle is taken per (manager, Environment) pair and its
 * subscription released with it. Without a manager, or without an
 * Environment, the list is one frozen empty array.
 */
export function useEnvironmentViews(
    environment: Environment | undefined
): readonly PlaneView[] {
    const viewManager = useRecoilValue(viewManagerState);
    const store = useMemo(
        () =>
            viewManager && environment
                ? viewStoreOf(viewManager.viewsFor(environment))
                : undefined,
        [viewManager, environment]
    );
    return useSyncExternalStore(
        store?.subscribe ?? noSignal,
        store?.read ?? noViews
    );
}

/** A per-Environment subscription the view manager offers. */
type EnvironmentSubscription = (
    viewManager: ViewManager,
    environment: Environment,
    listener: () => void
) => () => void;

/**
 * Re-renders the caller on every notification `subscribe` gives for
 * `environment` on the current view manager. Does nothing without a view
 * manager or an Environment. `subscribe` must keep its identity.
 */
function useEnvironmentSubscriptionVersion(
    environment: Environment | undefined,
    subscribe: EnvironmentSubscription
): void {
    const viewManager = useRecoilValue(viewManagerState);
    const subscribeToEnvironment = useMemo(
        () =>
            viewManager && environment
                ? (listener: () => void) =>
                      subscribe(viewManager, environment, listener)
                : undefined,
        [viewManager, environment, subscribe]
    );
    useSubscriptionVersion(subscribeToEnvironment);
}

const subscribeFrameRotation: EnvironmentSubscription = (
    viewManager,
    environment,
    listener
) => viewManager.subscribeFrameRotation(environment, listener);

const subscribeFusedImageDisplay: EnvironmentSubscription = (
    viewManager,
    environment,
    listener
) => viewManager.subscribeFusedImageDisplay(environment, listener);

/**
 * Re-renders the caller each time the frame of `environment` is turned in
 * place, which moves every view of it onto or off the acquired slices.
 * Does nothing without a view manager or an Environment.
 */
export function useFrameRotationVersion(
    environment: Environment | undefined
): void {
    useEnvironmentSubscriptionVersion(environment, subscribeFrameRotation);
}

/**
 * Re-renders the caller each time a view of `environment` changes which
 * image of a fused pair it draws, which moves it onto or off the primary
 * image. Does nothing without a view manager or an Environment.
 */
export function useFusedImageDisplayVersion(
    environment: Environment | undefined
): void {
    useEnvironmentSubscriptionVersion(environment, subscribeFusedImageDisplay);
}

/**
 * Whether some view of `environment` shows an acquired slice of its
 * primary image ({@link viewShowsAcquiredPrimarySlice}). False without an
 * Environment and while none of its views is on screen. Re-renders on
 * every change that can move a view onto or off those slices: a view of
 * it mounting or unmounting, its frame turning in place, and a view of it
 * changing which image of a fused pair it draws.
 */
export function useAcquiredPrimarySliceShown(
    environment: Environment | undefined
): boolean {
    const views = useEnvironmentViews(environment);
    useFrameRotationVersion(environment);
    useFusedImageDisplayVersion(environment);
    return views.some(viewShowsAcquiredPrimarySlice);
}

/**
 * Whether `environment` is on screen and every view of it draws one image
 * of its pair alone, so no view shows the two images blended. False
 * without an Environment and while none of its views is on screen, so a
 * row rehung through an empty moment does not read as drawn apart.
 * Re-renders when a view of it mounts or unmounts, or changes which image
 * of the pair it draws.
 */
export function useFusedPairDrawnApart(
    environment: Environment | undefined
): boolean {
    const views = useEnvironmentViews(environment);
    useFusedImageDisplayVersion(environment);
    return (
        views.length > 0 &&
        views.every(
            (view) => view.fusedImageDisplay !== FusedImageDisplay.Blended
        )
    );
}
